import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MessageCircle,
  Users,
  MessagesSquare,
  Radio,
  ShieldCheck,
  Bot,
  User,
  LogOut,
} from "lucide-react";
import { BloqLogo } from "@/components/brand/BloqLogo";
import { Avatar } from "@/components/common/Avatar";
import { BetaFeedbackWidget } from "@/components/common/BetaFeedbackWidget";
import { PreviewModeBadge } from "@/components/common/PreviewModeBadge";
import { TipCelebrationProvider, useTipCelebration } from "@/components/chat/TipCelebration";
import { FoundingWatcher } from "@/components/referrals/FoundingWatcher";
import { TrustBandWatcher } from "@/components/common/TrustBandWatcher";
import { NewLoginWatcher } from "@/components/common/NewLoginWatcher";
import { CipherHealWatcher } from "@/components/common/CipherHealWatcher";
import { UpsellSheet } from "@/components/common/UpsellSheet";
import { useRealtime, RealtimeProvider } from "@/context/RealtimeContext";
import { CallProvider } from "@/context/CallContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { maybeShowInviteNudge } from "@/lib/inviteNudge";
import { MICROTX_ENABLED } from "@/config";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/app/chats", label: "Chats", icon: MessageCircle, testid: "nav-chats" },
  { to: "/app/bloqs", label: "BLOQs", icon: Users, testid: "nav-bloqs" },
  { to: "/app/circles", label: "Circles", icon: MessagesSquare, testid: "nav-circles" },
  { to: "/app/channels", label: "Channels", icon: Radio, testid: "nav-channels" },
  { to: "/app/shield", label: "Shield", icon: ShieldCheck, testid: "nav-shield" },
  { to: "/app/assistant", label: "Assistant", icon: Bot, testid: "nav-assistant" },
  { to: "/app/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

// After returning from a tip Stripe Checkout, poll status once to settle + notify the recipient,
// then fire the sender-side "Tip sent" celebration (mirrors the mobile success state).
function TipReturnHandler() {
  const { celebrate } = useTipCelebration();
  useEffect(() => {
    if (!MICROTX_ENABLED) return;
    const raw = localStorage.getItem("bloq_pending_tip");
    if (!raw) return;
    localStorage.removeItem("bloq_pending_tip");
    let sid = raw;
    let meta = {};
    try {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.session_id) { sid = parsed.session_id; meta = parsed; }
    } catch { /* legacy plain session-id string */ }
    api.tipCheckoutStatus(sid)
      .then((res) => {
        if (res?.status !== "paid") return;
        toast.success("Tip sent 🎉");
        maybeShowInviteNudge();
        celebrate({
          variant: "sent",
          amount: meta.amount ?? res?.tip?.amount,
          handle: meta.handle ?? res?.tip?.to?.handle,
          message: meta.message,
        });
      })
      .catch(() => { /* silent — user can retry from the chat */ });
  }, [celebrate]);
  return null;
}

function GlobalRealtime() {
  const { subscribe } = useRealtime();
  const { celebrate } = useTipCelebration();
  const qc = useQueryClient();
  useEffect(() => {
    return subscribe((evt) => {
      if (!evt?.type) return;
      if (evt.type === "tip.received") {
        const t = evt.tip || {};
        celebrate({ variant: "received", amount: t.amount, handle: t.from?.handle, message: t.message });
      } else if (evt.type === "message.new") {
        const convId = evt.message?.conversation_id;
        if (convId) qc.invalidateQueries({ queryKey: ["messages", convId] });
        qc.invalidateQueries({ queryKey: ["chats"] });
        qc.invalidateQueries({ queryKey: ["bloqs"] });
        qc.invalidateQueries({ queryKey: ["badge-count"] });
      } else if (evt.type?.startsWith("room") || evt.type?.startsWith("circle")) {
        qc.invalidateQueries({ queryKey: ["bloqs"] });
      } else if (evt.type?.startsWith("connection")) {
        qc.invalidateQueries({ queryKey: ["requests"] });
        qc.invalidateQueries({ queryKey: ["contacts"] });
      }
    });
  }, [subscribe, qc, celebrate]);
  return null;
}

function CountBadge({ count, pulse, mobile, variant = "live", testidBase }) {
  if (!count) return null;
  const isUnread = variant === "unread";
  return (
    <span className={cn("relative flex items-center", mobile ? "absolute -right-2.5 -top-1" : "ml-auto")}>
      {pulse && (
        <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full", isUnread ? "bg-bloq-mint/60" : "bg-destructive/70")} />
      )}
      <span
        className={cn(
          "relative flex items-center justify-center rounded-full font-bold",
          isUnread ? "bg-bloq-mint text-bloq-bg" : "bg-destructive text-white",
          mobile ? "h-4 min-w-4 px-1 text-[9px]" : "h-5 min-w-5 px-1.5 text-[10px]",
        )}
        data-testid={mobile ? `${testidBase}-mobile` : testidBase}
        title={isUnread ? `${count} unread` : `${count} room${count === 1 ? "" : "s"} live now`}
      >
        {count > 99 ? "99+" : count}
      </span>
    </span>
  );
}

function SidebarLink({ item, badge, pulse, variant, testidBase }) {
  return (
    <NavLink
      to={item.to}
      data-testid={item.testid}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition-all",
          isActive
            ? "bg-bloq-mint/12 text-bloq-mint"
            : "text-bloq-muted hover:bg-white/5 hover:text-white",
        )
      }
    >
      {({ isActive }) => (
        <>
          <item.icon className={cn("h-5 w-5", isActive && "text-bloq-mint")} />
          <span>{item.label}</span>
          <CountBadge count={badge} pulse={pulse} variant={variant} testidBase={testidBase} />
        </>
      )}
    </NavLink>
  );
}

function BottomTab({ item, badge, pulse, variant, testidBase }) {
  return (
    <NavLink
      to={item.to}
      data-testid={`${item.testid}-mobile`}
      className={({ isActive }) =>
        cn(
          "flex flex-1 flex-col items-center gap-1 py-2 text-[10px] font-medium transition-colors",
          isActive ? "text-bloq-mint" : "text-bloq-muted",
        )
      }
    >
      <span className="relative">
        <item.icon className="h-5 w-5" />
        <CountBadge count={badge} pulse={pulse} mobile variant={variant} testidBase={testidBase} />
      </span>
      <span>{item.label}</span>
    </NavLink>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Detail routes render their own bottom-right composer/send button — hide the floating
  // feedback pill there so it can't overlap and steal the send click (see iteration_30).
  const hasComposer =
    /^\/app\/(chats|bloqs|channels)\/[^/]+/.test(location.pathname) ||
    location.pathname === "/app/assistant";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const { data: bloqsData } = useQuery({
    queryKey: ["bloqs"],
    queryFn: api.listBloqs,
    refetchInterval: 45000,
    staleTime: 20000,
  });
  const liveCount = (bloqsData?.mine || []).filter((c) => c.active_room).length;
  const { data: badgeData } = useQuery({
    queryKey: ["badge-count"],
    queryFn: api.badgeCount,
    refetchInterval: 30000,
    staleTime: 15000,
  });
  const unreadCount = badgeData?.count || 0;
  const prevLive = useRef(0);
  const [livePulse, setLivePulse] = useState(false);
  useEffect(() => {
    if (liveCount > prevLive.current) {
      setLivePulse(true);
      const t = setTimeout(() => setLivePulse(false), 4500);
      prevLive.current = liveCount;
      return () => clearTimeout(t);
    }
    prevLive.current = liveCount;
    return undefined;
  }, [liveCount]);

  // Reflect unread count in the browser tab title as a retention nudge, e.g. "(3) BLOQ Talk …".
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\)\s*/, "");
    document.title = unreadCount > 0 ? `(${unreadCount > 99 ? "99+" : unreadCount}) ${base}` : base;
    return () => {
      document.title = document.title.replace(/^\(\d+\+?\)\s*/, "");
    };
  }, [unreadCount]);

  const badgeFor = (item) => {
    // v1.3.4 (cc_5): top-level `count` now folds in BLOQ unreads — map each tab to its own granular field.
    if (item.to === "/app/chats")
      return { badge: badgeData?.dm_msgs || 0, pulse: false, variant: "unread", testidBase: "nav-chats-unread-badge" };
    if (item.to === "/app/circles")
      return { badge: badgeData?.group_msgs || 0, pulse: false, variant: "unread", testidBase: "nav-circles-unread-badge" };
    if (item.to === "/app/bloqs") {
      if (liveCount > 0)
        return { badge: liveCount, pulse: livePulse, variant: "live", testidBase: "nav-bloqs-live-badge" };
      return { badge: badgeData?.circle_msgs || 0, pulse: false, variant: "unread", testidBase: "nav-bloqs-unread-badge" };
    }
    return { badge: 0 };
  };

  return (
    <RealtimeProvider>
      <TipCelebrationProvider>
      <CallProvider>
      <GlobalRealtime />
      <TipReturnHandler />
      <FoundingWatcher />
      <TrustBandWatcher />
      <NewLoginWatcher />
      <CipherHealWatcher />
      <UpsellSheet />
      <PreviewModeBadge />
      <div className="flex h-screen w-full overflow-hidden bg-bloq-bg text-white">
        {/* Desktop sidebar */}
        <aside className="hidden w-[248px] shrink-0 flex-col border-r border-white/8 bg-bloq-s1 p-4 md:flex">
          <div className="flex items-center py-3">
            <BloqLogo size={24} />
          </div>
          <nav className="mt-4 flex flex-1 flex-col gap-1.5">
            {NAV.map((item) => (
              <SidebarLink key={item.to} item={item} {...badgeFor(item)} />
            ))}
          </nav>

          {user && (
            <div className="mt-3 rounded-2xl border border-white/8 bg-bloq-s2 p-3">
              <NavLink
                to="/app/profile"
                className="flex items-center gap-3"
                data-testid="sidebar-profile-card"
              >
                <Avatar user={user} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{user.display_name}</div>
                  <div className="truncate text-xs text-bloq-muted">@{user.username}</div>
                </div>
              </NavLink>
              <button
                onClick={handleLogout}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-white/8 py-2 text-xs text-bloq-muted transition-colors hover:text-white"
                data-testid="sidebar-logout-btn"
              >
                <LogOut className="h-3.5 w-3.5" /> Sign out
              </button>
            </div>
          )}
        </aside>

        {/* Main content */}
        <main className="flex min-w-0 flex-1 flex-col overflow-hidden pb-16 md:pb-0">
          <Outlet />
        </main>

        {/* Mobile bottom tabs */}
        <nav className="fixed inset-x-0 bottom-0 z-30 flex items-stretch border-t border-white/8 bg-bloq-s1/95 backdrop-blur-xl md:hidden">
          {NAV.map((item) => (
            <BottomTab key={item.to} item={item} {...badgeFor(item)} />
          ))}
        </nav>

        {/* Floating in-app beta feedback → admin beta feedback queue (hidden on composer routes) */}
        {!hasComposer && <BetaFeedbackWidget />}
      </div>
      </CallProvider>
      </TipCelebrationProvider>
    </RealtimeProvider>
  );
}
